// Uses the existing theme cart updater: it preserves line properties, applies
// Appstle on the server, refreshes totals and reports stock errors natively.
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

// Appstle's generic refresh requests /?sections=... and replaces the drawer
// wrapper. Horizon needs a localized SRA response and its native cart event.
// Install as soon as Appstle is ready, including normal product additions.
function connectAppstleRefresh() {
  const config = window._ABConfig;
  if (!config?.triggerCartRefresh || config.triggerCartRefresh.drydryNativeCart) return;
  const refresh = async () => {
    const form = document.querySelector('#cart-drawer form.cart-form');
    if (!form) return;
    const cartUrl = new URL(form.getAttribute('action'), location.origin);
    const root = cartUrl.pathname.replace(/\/cart\/?$/, '/');
    const wasOpen = Boolean(document.querySelector('#cart-drawer')?.hasAttribute('open'));
    let dismissed = false;
    const closeListener = event => { if (event.target.id === 'cart-drawer') dismissed = true; };
    document.addEventListener('theme-drawer:close', closeListener);
    const pending = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: pending.promise }));
    try {
      const responses = await Promise.all([
        fetch(`${root}?sections=cart-drawer-section`, { credentials: 'same-origin' }),
        fetch(`${cartUrl.pathname}.js`, { credentials: 'same-origin' }),
      ]);
      if (responses.some(response => !response.ok)) throw new Error('Cart refresh failed');
      const [sections, cart] = await Promise.all(responses.map(response => response.json()));
      if (!sections['cart-drawer-section']) throw new Error('Cart section missing');
      pending.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections, items: cart.items, itemCount: cart.item_count, source: 'drydry-bundle-upgrade', didError: false } });
      requestAnimationFrame(() => {
        const drawer = document.querySelector('#cart-drawer');
        if (wasOpen && !dismissed && drawer && !drawer.isOpen) drawer.open();
        document.removeEventListener('theme-drawer:close', closeListener);
      });
    } catch (error) {
      pending.reject(error);
      document.removeEventListener('theme-drawer:close', closeListener);
      console.warn('[DryDry bundle upgrade] Cart refresh failed', error);
    }
  };
  refresh.drydryNativeCart = true;
  config.triggerCartRefresh = refresh;
  // The generic wrapper guesses visibility from the section wrapper and can
  // reopen a deliberately closed drawer. Our refresh preserves native state.
  config.refreshCartDrawer = refresh;
  config.openCartDrawer = () => {
    const drawer = document.querySelector('#cart-drawer');
    if (drawer && !drawer.isOpen) drawer.open();
  };
}
// Preserve the drawer for ordinary theme additions too, not just upgrades.
// A customer's close action always takes precedence over an in-flight update.
document.addEventListener(StandardEvents.cartLinesUpdate, update => {
  connectAppstleRefresh();
  const drawer = document.querySelector('#cart-drawer');
  if (!update.promise || !drawer || (!drawer.hasAttribute('open') && update.action !== 'add')) return;
  const pending = new AbortController();
  let dismissed = false;
  document.addEventListener('theme-drawer:close', e => { if (e.target.id === 'cart-drawer') dismissed = true; }, { signal: pending.signal });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') dismissed = true; }, { signal: pending.signal });
  document.addEventListener('click', e => {
    if (e.target.closest?.('.cart-drawer__close-button, .theme-drawer__close-button, [data-testid="cart-drawer-trigger"]')) dismissed = true;
  }, { signal: pending.signal });
  update.promise.then(result => requestAnimationFrame(() => {
    const current = document.querySelector('#cart-drawer');
    if (!dismissed && !result?.detail?.didError && current && !current.isOpen) current.open();
    pending.abort();
  }), () => pending.abort());
});
document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-drydry-bundle-target]');
  if (!button) return;
  const component = button.closest('cart-items-component');
  const row = button.closest('tr[data-key]');
  if (!component || !row || typeof component.updateQuantity !== 'function') return;
  event.preventDefault();
  if (component.classList.contains('cart-items-disabled')) return;
  const rows = [...component.querySelectorAll('tr[data-key]')];
  const line = rows.indexOf(row) + 1;
  const quantity = Number(button.dataset.drydryBundleTarget);
  if (line < 1 || ![2, 3].includes(quantity)) return;
  connectAppstleRefresh();
  const wasOpen = Boolean(component.closest('theme-drawer')?.hasAttribute('open'));
  const pending = new AbortController();
  let dismissed = false;
  document.addEventListener('theme-drawer:close', e => { if (e.target.id === 'cart-drawer') dismissed = true; }, { signal: pending.signal });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') dismissed = true; }, { signal: pending.signal });
  document.addEventListener('click', e => {
    if (e.target.closest?.('.cart-drawer__close-button, .theme-drawer__close-button, [data-testid="cart-drawer-trigger"]')) dismissed = true;
  }, { signal: pending.signal });
  component.addEventListener(StandardEvents.cartLinesUpdate, update => {
    const restore = () => requestAnimationFrame(() => {
      const drawer = document.querySelector('#cart-drawer');
      if (wasOpen && !dismissed && drawer && !drawer.isOpen) drawer.open();
      pending.abort();
    });
    update.promise?.then(restore, restore);
  }, { once: true, signal: pending.signal });
  component.updateQuantity({ line, quantity, action: 'change' });
});

// Do not display a clickable offer until the native updater and Appstle's
// refresh integration are ready. Normal cart controls remain available.
async function showReadyOffers() {
  await customElements.whenDefined('cart-items-component');
  for (let attempt = 0; attempt < 100; attempt += 1) {
    if (window._ABConfig?.triggerCartRefresh) {
      connectAppstleRefresh();
      document.documentElement.classList.add('drydry-bundle-ready');
      return;
    }
    await new Promise(resolve => setTimeout(resolve, 100));
  }
}
showReadyOffers();
